import { SearchInput } from '@/components/ui'
import { RECONCILIATION_STATUSES } from '@/modules/projects/constants'
import type { ReconciliationStatus } from '@/modules/projects/stores'
import { RECONCILIATION_LABELS } from '../ReconciliationBadge'
import { Tally } from '../Tally'
import { type CycleAccountRow, countByStatusLabel } from './cycleRows'

// The verdicts worth a headline, worst first, each with the dot colour its badge uses.
const TALLIED: { status: ReconciliationStatus; dotClassName: string }[] = [
  { status: RECONCILIATION_STATUSES.needsReview, dotClassName: 'bg-amber-500' },
  {
    status: RECONCILIATION_STATUSES.unverifiable,
    dotClassName: 'bg-slate-400',
  },
  { status: RECONCILIATION_STATUSES.unknown, dotClassName: 'bg-neutral-400' },
  {
    status: RECONCILIATION_STATUSES.reconciled,
    dotClassName: 'bg-emerald-500',
  },
]

interface CycleToolbarProps {
  rows: CycleAccountRow[]
  query: string
  onQueryChange: (query: string) => void
  statusFilter: string | null
  onStatusFilterChange: (status: string | null) => void
  isReconciliationEntitled: boolean
}

// How many cycle-accounts sit at each verdict, with a one-click way to see only those, beside the
// search box and the sort control.
export const CycleToolbar = ({
  rows,
  query,
  onQueryChange,
  statusFilter,
  onStatusFilterChange,
  isReconciliationEntitled,
}: CycleToolbarProps) => {
  const counts = countByStatusLabel(rows)
  const tallies = isReconciliationEntitled
    ? TALLIED.filter(({ status }) => counts.has(RECONCILIATION_LABELS[status]))
    : []

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="flex flex-wrap items-center gap-1 text-sm">
        {tallies.length > 0 ? (
          tallies.map(({ status, dotClassName }) => {
            const label = RECONCILIATION_LABELS[status]
            return (
              <Tally
                key={status}
                label={label.toLowerCase()}
                count={counts.get(label) ?? 0}
                dotClassName={dotClassName}
                isActive={statusFilter === label}
                onClick={() =>
                  onStatusFilterChange(statusFilter === label ? null : label)
                }
              />
            )
          })
        ) : (
          <span className="text-muted-foreground">
            {rows.length === 1 ? '1 cycle' : `${rows.length} cycles`}
          </span>
        )}
      </div>

      <SearchInput
        value={query}
        onChange={onQueryChange}
        placeholder="Search accounts, dates, files"
        className="w-56"
      />
    </div>
  )
}
